<template>
  <v-app-bar flat color="#005AA9" dark height="80">
    <div class="logo-container">
      <v-img
          :src="require('../assets/christiani-logo-klein.png')"
          contain
          height="40"
          width="120"
      />
    </div>

    <v-toolbar-title class="white--text ml-4">
      <div class="case-title">{{ caseTitle }}</div>
      <div class="page-title text-caption">{{ $t(pageTitle) }}</div>
    </v-toolbar-title>

    <v-spacer></v-spacer>

    <!-- Navigation Leiste -->
    <nav class="mode-navigation">
      <a
        v-if="showCourseSelection"
        :href="generateUrl('UserCourseSelection')"
        class="nav-item"
        :class="{ active: route.name && (route.name === 'UserCourseSelection' || route.name === 'UserSessionSelection') }"
        @click.prevent="router.push(generateUrl('UserCourseSelection'))"
      >
        {{ $t('Meine Kurse') }}
      </a>
      
      <div v-if="showCourseSelection && hasModeEntries" class="nav-separator"></div>

      <a
        v-if="showLernen"
        :href="generateUrl('Lernen')"
        class="nav-item"
        :class="{ active: route.name === 'Lernen' }"
        @click.prevent="router.push(generateUrl('Lernen'))"
      >
        {{ $t('Lernen') }}
      </a>
      <a
        v-if="showUeben"
        :href="generateUrl('Ueben')"
        class="nav-item"
        :class="{ active: route.name === 'Ueben' }"
        @click.prevent="router.push(generateUrl('Ueben'))"
      >
        {{ $t('Ueben') }}
      </a>
      <a
        v-if="showTest"
        :href="generateUrl('Test')"
        class="nav-item"
        :class="{ active: route.name === 'Test' }"
        @click.prevent="router.push(generateUrl('Test'))"
      >
        {{ $t('Test') }}
      </a>

      <template v-if="isInstructor">
        <div v-if="hasVisibleNavBeforeInstructor" class="nav-separator"></div>

        <a :href="generateUrl('EvaluationCourseSelection')" class="nav-item setup-item" :class="{ active: route.name && route.name.startsWith('Evaluation') }" @click.prevent="router.push(generateUrl('EvaluationCourseSelection'))">
          {{ $t('Auswertung') }}
        </a>
      </template>

      <template v-if="canManageInspectionPoints">
        <div v-if="hasVisibleNavBeforeInspectionPoints" class="nav-separator"></div>

        <a :href="generateUrl('OwnerCaseSelection', 'Inspection Points')" class="nav-item setup-item" :class="{ active: route.name === 'Inspection Points' }" @click.prevent="router.push(generateUrl('OwnerCaseSelection', 'Inspection Points'))">
          {{ $t('Inspection Points') }}
        </a>
      </template>
    </nav>

    <v-spacer></v-spacer>

    <div class="header-actions">
      <LanguageSwitcher></LanguageSwitcher>
      <v-menu v-if="!isExternalFrame" offset-y>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props" class="ml-4">
            <v-icon color="white">mdi-cog</v-icon>
          </v-btn>
        </template>
        <v-list min-width="200">
          <v-list-item v-if="userName">
            <template v-slot:prepend>
              <v-icon>mdi-account</v-icon>
            </template>
            <v-list-item-title class="font-weight-bold">{{ userName }}</v-list-item-title>
            <v-list-item-subtitle v-if="userRole">{{ $t(userRole) }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider v-if="userName"></v-divider>
          
          <v-list-item v-if="isInstructor" :to="{ name: 'Settings' }">
            <template v-slot:prepend>
              <v-icon>mdi-cog</v-icon>
            </template>
            <v-list-item-title>{{ $t('Settings') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item v-if="canManageInspectionPoints" @click.prevent="router.push(generateUrl('OwnerCaseSelection', 'Inspection Points'))">
            <template v-slot:prepend>
              <v-icon>mdi-format-list-bulleted</v-icon>
            </template>
            <v-list-item-title>{{ $t('Inspection Points') }}</v-list-item-title>
          </v-list-item>
          
          <v-divider v-if="isInstructor"></v-divider>
          
          <v-list-item @click="logout">
            <template v-slot:prepend>
              <v-icon color="red">mdi-logout</v-icon>
            </template>
            <v-list-item-title class="red--text">{{ $t('Logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script>
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import { useRoute } from "vue-router";
import { useStore } from 'vuex';
import { ref, watch, computed } from "vue";
import APIServiceRestDB from "@/services/MesskursBackendService";
import { useRouter } from "vue-router";

export default {
  components: {
    LanguageSwitcher
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const pageTitle = ref('Messkurs Metalltechnik');
    const caseTitle = ref("-");
    const route = useRoute();

    const isAdmin = computed(() => {
      return store.state.user?.role === 'admin';
    });

    const isInstructor = computed(() => {
      const user = store.state.user;
      return user?.role === 'admin'
        || user?.role === 'instructor'
        || user?.role === 'licenceManager'
        || user?.hasTeamLicences === true
        || user?.hasFrontendManagedLicences === true
        || user?.messkursInstructorAccess === true;
    });

    const canManageInspectionPoints = computed(() => {
      const user = store.state.user;
      return isAdmin.value
        || user?.role === 'licenceManager'
        || user?.hasTeamLicences === true
        || user?.hasFrontendManagedLicences === true
        || user?.messkursInstructorAccess === true;
    });

    const userName = computed(() => {
      console.log("User state:", store.state.user);
      return store.state.user?.name || store.state.user?.username || store.state.user?.email;
    });

    const userRole = computed(() => {
      return store.state.user?.role;
    });

    const isExternalFrame = computed(() => store.state.isExternalFrame);
    const activeSession = computed(() => store.state.session || null);
    const hasOtpSessionContext = computed(() => {
      return !!(route.query.session || store.state.sessionId || route.query.case || store.state.caseId);
    });
    const isClearningRestrictedContext = computed(() => {
      if (isInstructor.value) return false;
      if (store.state.isExternalFrame || route.query.fromapp === '1' || route.query.iframe === '1') return true;
      return store.state.user?.isSSO === true && hasOtpSessionContext.value;
    });

    const showCourseSelection = computed(() => !isClearningRestrictedContext.value);
    const showLernen = computed(() => {
      if (!isClearningRestrictedContext.value) return true;
      return activeSession.value?.study === true;
    });
    const showUeben = computed(() => {
      if (!isClearningRestrictedContext.value) return true;
      return activeSession.value?.practice === true;
    });
    const showTest = computed(() => {
      if (!isClearningRestrictedContext.value) return true;
      return activeSession.value?.test === true;
    });
    const hasModeEntries = computed(() => showLernen.value || showUeben.value || showTest.value);
    const hasVisibleNavBeforeInstructor = computed(() => showCourseSelection.value || hasModeEntries.value);
    const hasVisibleNavBeforeInspectionPoints = computed(() => {
      if (isInstructor.value) return true;
      return hasVisibleNavBeforeInstructor.value;
    });

    watch(() => route.name, (newRouteName) => {
      pageTitle.value = newRouteName;
    });

    watch(() => store.state.actualCase, (newCase) => {
      if (newCase && (newCase.title || newCase.name)) {
        caseTitle.value = newCase.title || newCase.name;
      }
    }, { immediate: true });

    watch(() => store.state.caseId, async (newCaseId) => {
      if (!newCaseId) return;
      try {
        const object = await APIServiceRestDB.getObjectWithId(newCaseId);
        if (object) {
          caseTitle.value = object.title;
        }
      } catch (error) {
        console.error("Error fetching case:", error);
      }
    });

    const generateUrl = (routeName, mode = null) => {
      let path = '/';
      switch (routeName) {
        case 'Lernen': path = '/lernen'; break;
        case 'Ueben': path = '/ueben'; break;
        case 'Test': path = '/test'; break;
        case 'Inspection Points': path = '/inspectionpoints'; break;
        case 'EvaluationCourseSelection': path = '/evaluation'; break;
        case 'UserCourseSelection': path = '/selection'; break;
        case 'OwnerCaseSelection': path = '/selection/cases'; break;
      }

      const params = new URLSearchParams();
      
      // Use case and session from store if available
      if (store.state.caseId) {
        params.set('case', store.state.caseId);
      }
      if (store.state.sessionId) {
        params.set('session', store.state.sessionId);
      }

      if (mode) {
        params.set('mode', mode);
      }
      const queryString = params.toString();
      return path + (queryString ? '?' + queryString : '');
    };

    const logout = () => {
      store.dispatch('logout');
      router.push({ name: 'Login' });
    };

    return {
      pageTitle,
      caseTitle,
      route,
      router,
      generateUrl,
      logout,
      isAdmin,
      isInstructor,
      canManageInspectionPoints,
      userName,
      userRole,
      isExternalFrame,
      showCourseSelection,
      showLernen,
      showUeben,
      showTest,
      hasModeEntries,
      hasVisibleNavBeforeInstructor,
      hasVisibleNavBeforeInspectionPoints
    };
  }
}
</script>

<style scoped>
.logo-container {
  padding-left: 16px;
  background-color: white;
  height: 100%;
  display: flex;
  align-items: center;
  padding-right: 16px;
  clip-path: polygon(0 0, 100% 0, 85% 100%, 0% 100%);
}

.case-title {
  font-weight: bold;
  font-size: 1.1rem;
}

.mode-navigation {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 4px;
}

.nav-item {
  color: white;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background 0.2s;
  font-weight: 500;
  margin: 0 2px;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.2);
}

.nav-item.active {
  background: white;
  color: #005AA9;
}

.nav-separator {
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 8px;
}

.setup-item {
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.setup-item.active {
  border-color: white;
}

.header-actions {
  display: flex;
  align-items: center;
  padding-right: 16px;
}
</style>
